import { useTranslations } from '@vocab/react';
import React, { type ReactNode, useState } from 'react';
import Cookies from 'universal-cookie';

import { StaticPageProvider } from 'src/components';
import { getQueryString } from 'src/shared/helpers';

import translations from './.vocab';
import { ChromeLogo, EdgeLogo, FirefoxLogo, SafariLogo } from './Browsers';
import SeekSVG from './components/SEEKSvg';

import * as styles from './styles.css';

function isSameOrigin(uri: string) {
  if (typeof uri !== 'string' || typeof document === 'undefined') {
    return false;
  }
  const testLink = document.createElement('a');
  testLink.href = uri;
  // Cannot use .origin as this requires IE11
  return (
    testLink.host === location.host && testLink.protocol === location.protocol
  );
}
interface BrowserProps {
  logo: any;
  title: string;
  link: string;
  mobileLink?: string;
}

const Browser = ({ logo, title, link, mobileLink }: BrowserProps) => {
  const { t } = useTranslations(translations);
  const Logo = logo;

  return (
    <div className={styles.browser}>
      <a href={link} target="_blank" rel="noreferrer">
        <Logo />
      </a>
      <h4 className={styles.h4}>{title}</h4>
      {mobileLink ? (
        <div>
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className={`${styles.desktopLink} ${styles.link}`}
          >
            {t('upgrade')}
          </a>
          <a
            href={mobileLink}
            target="_blank"
            rel="noreferrer"
            className={`${styles.mobileLink} ${styles.link}`}
          >
            {t('upgrade')}
          </a>
        </div>
      ) : (
        <a href={link} target="_blank" rel="noreferrer" className={styles.link}>
          {t('upgrade')}
        </a>
      )}
    </div>
  );
};

const Header = () => (
  <header className={styles.header}>
    <div className={styles.headerContainer}>
      <SeekSVG />
    </div>
  </header>
);

const Card = ({ children }: { children: ReactNode }) => (
  <div className={styles.card}>{children}</div>
);

const Text = ({ children }: { children: ReactNode }) => (
  <span className={styles.text}>{children}</span>
);

const StaticPageProps = {
  title: 'Browser Upgrade',
  currentPage: 'hirer-browser-upgrade',
  siteSection: 'other',
  returnUrl: '/browser-upgrade/',
  eventName: 'hirer-browser-upgrade-viewed',
  hideHeader: true,
  hideFooter: true,
};

const BrowserUpgrade = () => {
  const { t } = useTranslations(translations);
  const continueQuery = getQueryString('continue');
  const [continueURI] = useState(
    isSameOrigin(continueQuery) ? continueQuery : '/',
  );

  const leaveMeAlone = () => {
    const cookies = new Cookies();
    cookies.set('UpdateBrowserMessage', 1, { path: '/' });
    // @ts-ignore
    window.location = continueURI;
  };

  return (
    <StaticPageProvider {...StaticPageProps}>
      <div className={styles.page}>
        <Header />
        <Card>
          <h1 className={styles.h1}>{t('browser upgrade')}</h1>
          <div className={styles.section}>
            <h3 className={styles.h3}>{t('why am i seeing this page')}</h3>
            <Text>{t('youre seeing this page')}</Text>
            <Text>{t('we offer limited support')}</Text>
          </div>
          <div className={styles.section}>
            <h3 className={styles.h3}>{t('how do i upgrade')}</h3>
            <Text>{t('upgrading is free and easy')}</Text>
            <Text>{t('click on the link')}</Text>
            <Text>{t('to keep safari up to date')}</Text>
          </div>
          <div className={styles.section}>
            <div>
              <Browser
                title="Microsoft Edge"
                logo={EdgeLogo}
                link="https://www.microsoft.com/en-us/edge"
              />
              <Browser
                title="Firefox"
                logo={FirefoxLogo}
                link="https://support.mozilla.org/en-US/kb/update-firefox-latest-release"
              />
              <Browser
                title="Google Chrome"
                logo={ChromeLogo}
                link="https://www.google.com/chrome/update/"
              />
              <Browser
                title="Safari"
                logo={SafariLogo}
                link="https://support.apple.com/en-au/HT204416"
                mobileLink="https://support.apple.com/en-us/HT204204"
              />
            </div>
          </div>
        </Card>
        <div className={styles.continueContainer}>
          <span
            className={styles.continueButton}
            role="button"
            tabIndex={0}
            onClick={leaveMeAlone}
          >
            {t('continue and remind me later')}
          </span>
        </div>
        <div className={styles.copyrightFooter}>
          <span>© SEEK. {t('all rights reserved')}</span>
        </div>
      </div>
    </StaticPageProvider>
  );
};

export default BrowserUpgrade;
